import { useMemo } from "react";
import { moneyShort, summarizeUnits } from "./unitStats";
import "./portfolioTable.css";

/**
 * The summary tiles every mastersheet view shows above its table: units,
 * occupied, vacant, already rented (All Properties only — it needs each row's
 * `signed_lease` flag), basic rent, target rent, gap to target. `rows` is what is
 * currently visible; `totalRows` is the unfiltered count so the Units tile can
 * read "12 of 345 units" while a filter is narrowing the view.
 */
const MastersheetStats = ({ rows, totalRows, propertyCount, className = "", referenceDate }) => {
  // `referenceDate` (Historic sheet): "ending ≤60d" counts from that day, not today.
  const t = useMemo(() => summarizeUnits(rows, referenceDate), [rows, referenceDate]);
  const total = totalRows ?? rows.length;

  let unitsLabel;
  if (rows.length !== total) unitsLabel = `${rows.length} of ${total} units`;
  else if (propertyCount !== undefined && propertyCount !== null) {
    unitsLabel = `${total} units · ${propertyCount} propert${propertyCount === 1 ? "y" : "ies"}`;
  } else unitsLabel = `${total} units`;

  return (
    <div className={`pft-stats ${className}`.trim()} data-testid="mastersheet-stats">
      <div className="pft-stat">
        <div className="pft-stat__label">Units</div>
        <div className="pft-stat__value">{unitsLabel}</div>
      </div>
      <div className="pft-stat pft-stat--occupied">
        <div className="pft-stat__label">Occupied</div>
        <div className="pft-stat__value">{t.occupied}</div>
        <div className="pft-stat__sub">{t.occupancy}% occupancy</div>
      </div>
      <div className="pft-stat pft-stat--vacant">
        <div className="pft-stat__label">Vacant</div>
        <div className="pft-stat__value">{t.vacant}</div>
        <div className="pft-stat__sub">{t.expiring} lease{t.expiring === 1 ? "" : "s"} ending ≤60d</div>
      </div>
      {t.hasRentedInfo && (
        <div
          className="pft-stat pft-stat--rented"
          data-testid="stat-already-rented"
          title="Vacant units, and units whose lease ends within 60 days, that already have a fully signed new lease waiting to start. Renewals still in progress are not counted."
        >
          <div className="pft-stat__label">Already Rented</div>
          <div className="pft-stat__value">{t.rented}</div>
          <div className="pft-stat__sub">
            {t.rentedVacant} of {t.vacant} vacant · {t.rentedExpiring} of {t.expiring} ending
          </div>
        </div>
      )}
      <div className="pft-stat pft-stat--rent">
        <div className="pft-stat__label">Basic Rent</div>
        <div className="pft-stat__value">{moneyShort(t.basic)}</div>
        <div className="pft-stat__sub">per month</div>
      </div>
      <div className="pft-stat">
        <div className="pft-stat__label">Target Rent</div>
        <div className="pft-stat__value">{moneyShort(t.target)}</div>
        <div className="pft-stat__sub">per month</div>
      </div>
      <div className="pft-stat pft-stat--gap">
        <div className="pft-stat__label">Gap to Target</div>
        <div className="pft-stat__value">{moneyShort(t.gap)}</div>
        <div className="pft-stat__sub">▲{t.up} ~{t.near} ▼{t.down}</div>
      </div>
    </div>
  );
};

export default MastersheetStats;
